/**
 * Header live search: takes over the secondary menu's Search item as #ui-md-search.
 * Queries POST /wp-json/api/v2/content and sends submitted questions to Qualified.
 */

const ICON_SVG = '<svg class="ui-md-search__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 22 22" aria-hidden="true"><path fill="#002855" fill-rule="evenodd" d="M 18.77817 11.00000 A 7.77817 7.77817 0 1 0 3.22183 11.00000 A 7.77817 7.77817 0 1 0 18.77817 11.00000 M 17.27817 11.00000 A 6.27817 6.27817 0 1 1 4.72183 11.00000 A 6.27817 6.27817 0 1 1 17.27817 11.00000"></path><path fill="#002855" d="M 15.25284 15.73050 L 16.31350 14.66984 L 21.14823 19.50457 L 20.08757 20.56523 Z"></path></svg>';

const SPINNER_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28"><path fill-rule="evenodd" d="M14 .188c.587 0 1.063.475 1.063 1.062V5.5a1.063 1.063 0 0 1-2.126 0V1.25c0-.587.476-1.063 1.063-1.063ZM4.182 4.181a1.063 1.063 0 0 1 1.503 0L8.73 7.228A1.062 1.062 0 1 1 7.228 8.73L4.182 5.685a1.063 1.063 0 0 1 0-1.503Zm19.636 0a1.063 1.063 0 0 1 0 1.503L20.772 8.73a1.062 1.062 0 1 1-1.502-1.502l3.045-3.046a1.063 1.063 0 0 1 1.503 0ZM.188 14c0-.587.475-1.063 1.062-1.063H5.5a1.063 1.063 0 0 1 0 2.126H1.25A1.063 1.063 0 0 1 .187 14Zm21.25 0c0-.587.475-1.063 1.062-1.063h4.25a1.063 1.063 0 0 1 0 2.126H22.5A1.063 1.063 0 0 1 21.437 14ZM8.73 19.27a1.062 1.062 0 0 1 0 1.502l-3.045 3.046a1.063 1.063 0 0 1-1.503-1.503l3.046-3.046a1.063 1.063 0 0 1 1.502 0Zm10.54 0a1.063 1.063 0 0 1 1.502 0l3.046 3.045a1.063 1.063 0 0 1-1.503 1.503l-3.046-3.046a1.063 1.063 0 0 1 0-1.502ZM14 21.438c.587 0 1.063.475 1.063 1.062v4.25a1.063 1.063 0 0 1-2.126 0V22.5c0-.587.476-1.063 1.063-1.063Z"></path></svg>';

export function initNavSearch() {
  const link = document.querySelector('.mdh__secondary a[href*="search"]');
  if (!link) {
    return;
  }

  const li = link.closest('li');
  const lang = (document.documentElement.lang || 'en').split('-')[0];
  const resultsUrl = (query) => `/${lang}/?s=${encodeURIComponent(query)}`;

  li.innerHTML = `<div id="ui-md-search">
    ${ICON_SVG}
    <input class="ui-md-search__input" type="search" name="s" autocomplete="off" aria-label="Search">
    <div class="ui-md-search__results" role="listbox"></div>
  </div>`;

  const root = li.querySelector('#ui-md-search');
  const input = root.querySelector('.ui-md-search__input');
  const results = root.querySelector('.ui-md-search__results');

  let seq = 0;
  let debounce;

  function close() {
    root.classList.remove('ui-md-search--open');
    seq++;
    clearTimeout(debounce);
  }

  input.addEventListener('input', () => {
    const query = input.value.trim();

    if (query.length < 3) {
      close();
      return;
    }

    // Only paint the spinner when nothing is shown yet, so results do not blink on each keystroke.
    if (!root.classList.contains('ui-md-search--open')) {
      root.classList.add('ui-md-search--open');
      results.innerHTML = `<div class="e-search-loading"><div class="e-search-loader">${SPINNER_SVG}</div></div>`;
    }

    clearTimeout(debounce);
    debounce = setTimeout(() => fetchResults(query), 250);
  });

  input.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' && !event.isComposing && input.value.trim().length) {
      const query = input.value.trim();

      // Qualified is consent-gated and may also be disabled for this page or locale.
      // Keep the existing results page as the fallback whenever its API is unavailable.
      if (typeof window.qualified === 'function') {
        event.preventDefault();
        window.qualified('startChat', { initialMessage: query });
        close();
      } else {
        window.location.href = resultsUrl(query);
      }
    }
    if (event.key === 'Escape') {
      input.value = '';
      close();
      input.blur();
    }
  });

  document.addEventListener('mousedown', (event) => {
    if (!event.target.closest('#ui-md-search')) {
      close();
    }
  });

  async function fetchResults(query) {
    const mySeq = ++seq;
    const params = new URLSearchParams({
      post_type: 'any',
      post_per_page: '5',
      offset: '0',
      title_search: query,
      solr: 'true',
      lang: lang,
    });

    try {
      const response = await fetch('/wp-json/api/v2/content', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params,
      });
      const data = await response.json();
      if (mySeq !== seq) {
        return;
      }
      render(data, query);
    } catch (error) {
      if (mySeq !== seq) {
        return;
      }
      results.innerHTML = '<div class="e-search-no-results">Error performing search</div>';
    }
  }

  function render(data, query) {
    if (!data || !Array.isArray(data.data) || data.data.length === 0) {
      results.innerHTML = '<div class="e-search-no-results">No results found</div>';
      return;
    }

    let html = '<div class="e-search-results-list">';
    data.data.slice(0, 5).forEach((item) => {
      const type = item.typeTitle ? item.typeTitle.replace(/\[v\d+\]/gi, '').trim() : '';
      html += `<div class="e-search-result-item" data-url="${item.url}">
        <span class="e-search-result-type">${type}</span>
        <a href="${item.url}" class="e-search-result-title">${item.title}</a>
      </div>`;
    });
    if (data.count > 5) {
      html += `<a href="${resultsUrl(query)}" class="e-search-see-all">See All ${data.count} Results</a>`;
    }
    html += '</div>';
    results.innerHTML = html;

    // Navigate on mousedown so the click lands before the input blur closes the box.
    results.querySelectorAll('.e-search-result-item').forEach((item) => {
      item.addEventListener('mousedown', () => {
        window.location.href = item.dataset.url;
      });
    });
  }
}
